/**
 * AtlasAmbientLifeModule — what the `ambientLife` graphics setting switches (gaps wave round 2, 2026-09-28; the ambient
 * life wave, 2026-09-29).
 *
 * The preset flag had no reader since the 2026-09-25 realism wave (R12 deferred, its menu row removed). This module is
 * the R12 build (impl-vegetation.md "What is left" §1): falling leaves in the woods, pollen motes over meadows by day, a
 * dust devil in the windy desert, CPU-stepped on the wall clock (the fauna's AMBIENT TIME law), sized by the tier's
 * `particleCap`. The woods' insect swarm (AtlasFaunaModule), the herds (AtlasHerdsModule) and the critters
 * (AtlasCrittersModule) answer the same flag. Rules: `lib/ambient_life.ts`.
 * - LEAVES are lit, alpha-tested 3D quads that TUMBLE (`leafTumble`: they rock edge-on, roll and spin as they fall — a
 *   camera-facing sprite could only pretend by shrinking), one instanced mesh per silhouette, tinted per leaf. A leaf
 *   that lands flops flat, rests and DISSOLVES (`leafPose` / `leafAlpha`): a per-instance fade drives a screen-space
 *   dither under the alpha test (the house LOD-fade pattern, lib/instancing.ts), so the silhouette stays crisp while
 *   the leaf comes and goes — a spawn in view, a landing and the edge of the woods never pop.
 * - MOTES and the DEVIL's grains are pooled sprite batches (`living-events/fx_sprite_batch.ts`: one draw per kind,
 *   `markMrtOverlay`, a FIXED `count`) that FADE through a per-sprite opacity (`setOpacity`) — they used to shrink away.
 *   Motes keep their WORLD position (the viewer walks through the pollen) and fade in, out and toward the bubble's rim.
 * - The devil stands in a SKIRT of its own dust (`devilSkirt`) and is HEARD: one positional wind loop
 *   (`ambient_dust_devil`) at its column, its gain by `devilHeard` (its life × an inverse-distance law).
 * Live: the flag, the cap and the weather are read every tick; off = every leaf, mote and grain gone at once.
 * Frame: everything hangs under an anchor near the viewer (absolute coordinates) that is re-seated past 1.5 km, so
 * float32 never sees a 900 km coordinate.
 * Per frame: allocation-free (pooled leaves, scratch poses / grains, a reused count array).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { WeatherModule } from './WeatherModule.js';
import { uSunDir } from './SkyModule.js';
import { nightFactor } from '../lib/lighting.js';
import { forestDensity } from '../lib/forest_field.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { biomeAt } from '../../../shared/terrain.js';
import { activePreset } from '../lib/quality_tiers.js';
import { FxSpriteBatch, type FxSprite } from './living-events/fx_sprite_batch.js';
import { glowTexture } from './living-events/fx_textures.js';
import { leafTextures, dustTexture, leafMaterial, GRAIN_HEX, SKIRT_HEX } from '../lib/ambient_look.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import { sfxLoop, stopSfxScope, type LoopHandle } from '../lib/sfx.js';
import { waterStillAt } from '../lib/water_fx.js';
import { lakeSurfaceAtBaked } from '../lib/lakes.js';
import {
  ambientBudget, spawnLeaf, stepLeaf, leafOut, leafTouchdown, settleLeaf, leafGone, leafPose, leafAlpha, leafSpawnCount,
  spawnMote, stepMote, moteGlint, moteAlpha, releaseMote, moteGone,
  spawnDevil, stepDevil, devilGrain, devilSkirt, devilGap, devilAlpha, devilHeard, LEAF_TINTS, LEAF_EULER, DEVIL, DEVIL_SOUND,
  type AmbientBudget, type AmbientEnv, type Leaf, type LeafPose, type Mote, type Devil, type DevilSprite,
} from '../lib/ambient_life.js';

/** Re-seat the batches' anchor when the viewer is this far from it (m) — float32 stays sub-millimetre inside it. */
const REANCHOR_M = 1500;
/** Re-sample the spot (forest density, biome, weather) this often (s) — forestDensity is ~0.3 ms a sample. */
const ENV_EVERY_S = 1;
/** Above the ground by this much the viewer is "away" (a ship, fly mode) — the fauna's ALOFT height. */
const ALOFT_M = 45;
const LEAF_CAP_PER_MESH = 64;
const MOTE_CAP = 128;
const SOUND_SCOPE = 'ambient-life';
interface LeafMesh { mesh: THREE.InstancedMesh; fade: THREE.InstancedBufferAttribute }
interface Batches { anchorX: number; anchorY: number; anchorZ: number; group: THREE.Group; leaves: LeafMesh[]; motes: FxSpriteBatch; dust: FxSpriteBatch }

export class AtlasAmbientLifeModule implements IModule {
  readonly name = 'AtlasAmbientLife';
  private app: App | null = null;
  private scene: THREE.Scene | null = null;
  private env: EnvironmentModule | null = null;
  private weather: WeatherModule | null = null;
  private seed = '';
  private sea = 0;
  private batches: Batches | null = null;
  private readonly leaves: Leaf[] = [];
  /** Finished leaves, recycled by the next spawn (no allocation per leaf). */
  private readonly leafPool: Leaf[] = [];
  /** The probe lever's burst (diagnostics only: `__ambientLife().burst`). */
  private readonly burstLeaves = new Set<Leaf>();
  private readonly motes: Array<{ m: Mote; s: FxSprite }> = [];
  private devil: Devil | null = null;
  private readonly devilSprites: FxSprite[] = [];
  private devilWait = 8;
  private forceDevil = false;
  /** The live devil came from the probe lever (it lives out its life whatever the wind). */
  private devilForced = false;
  private devilLoop: LoopHandle | null = null;
  private devilGain = 0;
  private leafCarry = 0;
  private envIn = 0;
  private budget: AmbientBudget = { leaves: 0, pollen: 0, devil: false, why: 'not started' };
  private lastEnv: AmbientEnv | null = null;
  private gate: string | null = 'not started';
  private lastMs = 0;
  /** Probe lever: freeze the layer (the drawing holds its last frame) — a swiftshader frame is seconds long. */
  private held = false;
  /** Probe lever: the layer's clock rate (1 = the wall clock). */
  private timeScale = 1;
  private rngState = 0x9e3779b9;
  private readonly rng = (): number => { let t = (this.rngState += 0x6d2b79f5); t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  private readonly floorFn = (x: number, z: number): number => this.floorAt(x, z);
  private readonly stats = { leavesSpawned: 0, leavesLanded: 0, leavesFaded: 0, motesSpawned: 0, motesFaded: 0, devils: 0 };
  private readonly _m = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _e = new THREE.Euler();
  private readonly _p = new THREE.Vector3();
  private readonly _s = new THREE.Vector3();
  private readonly _c = new THREE.Color();
  private readonly _counts = [0, 0, 0];
  private readonly _pose: LeafPose = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };
  private readonly _gr: DevilSprite = { x: 0, y: 0, z: 0, size: 0, alpha: 0 };
  private readonly _at = { x: 0, y: 0, z: 0 };

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as THREE.Scene;
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:environment-updated', () => this.onWorld());
    app.world.addSystem(makeSystem('AtlasAmbientLife', Phase.Animation, 41, (dt) => this.tick(dt)));
    const g = globalThis as unknown as Record<string, unknown>;
    g.__ambientLife = () => this.diag();
    /** Probe lever: a dust devil forms near the viewer now (still needs the flag on and a particle budget). */
    g.__ambientLifeDevil = () => { this.forceDevil = true; this.devilWait = 0; return true; };
    /** Probe lever: drop `n` leaves (≤ 48) up to `dMax` m (default 6) ahead of the camera, `h` m over the ground (default 1–4) — close-up pictures. */
    g.__ambientLifeLeaves = (n = 16, h?: number, dMax = 6) => this.leafBurst(n, h, dMax);
    /** Probe lever: hold the layer still (`true`) or let it run (`false`). */
    g.__ambientLifeHold = (on: boolean) => { this.held = !!on; return this.held; };
    /** Probe lever: run the layer's clock at `k` × the wall clock (0.05–4; 1 = normal) — a swiftshader frame is seconds long. */
    g.__ambientLifeTimeScale = (k = 1) => { this.timeScale = Math.max(0.05, Math.min(4, Number(k) || 1)); return this.timeScale; };
    /** Probe lever: re-read the spot (budget, weather, biome) on the next tick instead of within the second. */
    g.__ambientLifeResample = () => { this.envIn = 0; return true; };
  }

  private onWorld(): void {
    const conn = atlas.conn;
    let seed = '';
    try { const id = atlas.currentWorldId; if (conn && id !== null) { const w = conn.db.world.worldId.find(id); if (w && w.kind === 'commons') seed = w.terrainSeed; } } catch { /* mid-churn */ }
    if (seed === this.seed) return;
    this.clearAll();
    this.seed = seed;
    this.sea = seed ? commonsSeaLevel(seed) : 0;
  }

  private groundAt(x: number, z: number): number { return this.env ? this.env.groundAt(x, z) : 0; }

  /**
   * What a leaf lands on and a mote hovers over: still water (the sea, a lake, a river) where it stands over the ground,
   * else the rendered ground (the fauna's flight floor). A leaf over a woodland lake landed on the BED and rested unseen
   * under the water (the world picture, 2026-09-29): it now settles on the surface and floats there while it dissolves.
   */
  private floorAt(x: number, z: number): number {
    const g = this.groundAt(x, z);
    const w = waterStillAt(x, z) ?? lakeSurfaceAtBaked(x, z);
    return w !== null && w > g ? w : g;
  }

  private tick(dtRaw: number): void {
    if (!this.seed && atlas.currentWorldId !== null) this.onWorld();
    const app = this.app;
    if (app) {
      this.env ??= (app.getModule?.('Environment') as EnvironmentModule | undefined) ?? null;
      this.weather ??= (app.getModule?.('Weather') as WeatherModule | undefined) ?? null;
    }
    const p = activePreset();
    this.gate = p.ambientLife === false ? 'ambientLife off'
      : !this.seed ? 'no world seed yet'
        : atlas.loadingGate ? 'loading gate is up'
          : atlas.currentWorldId !== atlas.commonsWorldId ? 'not in the Commons'
            : atlas.spaceFlight ? 'in space flight'
              : !this.env ? 'modules not resolved yet'
                : null;
    if (this.gate) { this.clearAll(); return; }
    // AMBIENT TIME: the wall clock (≤ 1 s a frame), never the engine's clamped dt (the fauna's law)
    const now = performance.now();
    const dt = Math.min(1, Math.max(0, this.lastMs ? (now - this.lastMs) / 1000 : dtRaw)) * this.timeScale;
    this.lastMs = now;
    if (this.held) return; // the probe's still frame: nothing moves, the last matrices and opacities stay drawn
    const px = atlas.localX, py = atlas.localY, pz = atlas.localZ;
    const w = this.weather?.wind();
    const wx = w?.x ?? 1, wz = w?.z ?? 0, ws = w?.strength ?? 0;
    this.envIn -= dt;
    if (this.envIn <= 0) {
      this.envIn = ENV_EVERY_S;
      const gy = this.floorAt(px, pz); // a swimmer over deep water is at the surface, not "away" 50 m over the bed
      const e: AmbientEnv = {
        enabled: p.ambientLife, particleCap: p.particleCap ?? 0, foliageDrawn: (p.foliageDensityScale ?? 1) > 0,
        forestDensity: forestDensity(this.seed, px, pz, this.sea), biome: biomeAt(this.seed, px, pz),
        night: nightFactor((uSunDir.value as THREE.Vector3).y),
        precipKind: w?.targetKind ?? 'none', precip: w?.targetDensity ?? 0, temperatureC: w?.temperatureC ?? null,
        windStrength: ws, indoors: atlas.interiorId !== null, away: py - gy > ALOFT_M,
      };
      this.lastEnv = e;
      this.budget = ambientBudget(e);
    }
    const b = this.ensureBatches(px, py, pz);
    this.stepLeaves(b, dt, px, pz, wx, wz, ws);
    this.stepMotes(b, dt, px, pz, wx, wz, ws);
    this.stepDevil(b, dt, px, pz, wx, wz, ws);
    b.motes.commit(); b.dust.commit();
  }

  private ensureBatches(px: number, py: number, pz: number): Batches {
    const cur = this.batches;
    if (cur && Math.hypot(px - cur.anchorX, pz - cur.anchorZ) < REANCHOR_M) return cur;
    // THE FRAME: a new anchor near the viewer — the old sprites (if any) are dropped and re-spawn around the new one
    this.clearAll();
    const scene = this.scene!;
    const ay = this.groundAt(px, pz);
    const anchor = new THREE.Vector3(px, ay, pz);
    const group = new THREE.Group();
    group.name = 'ambient-life';
    group.position.copy(anchor);
    const leaves = leafTextures().map((tex, k): LeafMesh => {
      const geo = new THREE.PlaneGeometry(1, 1);
      const fade = new THREE.InstancedBufferAttribute(new Float32Array(LEAF_CAP_PER_MESH), 1);
      fade.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('aFade', fade);
      const mesh = new THREE.InstancedMesh(geo, leafMaterial(tex, k), LEAF_CAP_PER_MESH);
      mesh.name = 'ambient-life-leaves';
      mesh.count = 0; mesh.frustumCulled = false; mesh.castShadow = false; mesh.receiveShadow = true; mesh.visible = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.setColorAt(0, this._c.set(0xffffff)); // allocate the instance colours up front
      group.add(mesh);
      return { mesh, fade };
    });
    const motes = new FxSpriteBatch(glowTexture(), MOTE_CAP, true, anchor.clone());
    const dust = new FxSpriteBatch(dustTexture(), DEVIL.grains + DEVIL.skirt, false, anchor.clone());
    for (const bb of [motes, dust]) bb.sprite.name = 'ambient-life';
    scene.add(group); scene.add(motes.sprite); scene.add(dust.sprite);
    group.updateMatrixWorld(true);
    this.batches = { anchorX: px, anchorY: ay, anchorZ: pz, group, leaves, motes, dust };
    void py;
    return this.batches;
  }

  private stepLeaves(b: Batches, dt: number, px: number, pz: number, wx: number, wz: number, ws: number): void {
    const target = this.budget.leaves;
    // spawn toward the target (a first burst when the woods start bare), never past the meshes' room. The target
    // counts the leaves resting on the ground too: they are drawn, so they spend the particle budget.
    const room = LEAF_CAP_PER_MESH * b.leaves.length;
    const sc = leafSpawnCount(this.leaves.length, Math.min(target, room), dt, this.leafCarry);
    this.leafCarry = sc.carry;
    for (let i = 0; i < sc.n; i++) { this.leaves.push(spawnLeaf(this.rng, px, pz, this.floorFn, this.leafPool.pop())); this.stats.leavesSpawned++; }
    const counts = this._counts;
    counts[0] = 0; counts[1] = 0; counts[2] = 0;
    for (let i = this.leaves.length - 1; i >= 0; i--) {
      const l = this.leaves[i];
      stepLeaf(l, dt, wx, wz, ws);
      let done = leafOut(l, px, pz);
      if (!done && l.settled < 0) {
        const gy = this.floorAt(l.x, l.z);
        // a landing no longer deletes the leaf (it vanished the frame it touched the grass): it settles, rests, dissolves
        if (leafTouchdown(l, gy)) { settleLeaf(l, gy); this.stats.leavesLanded++; }
      } else if (!done && leafGone(l)) { done = true; this.stats.leavesFaded++; }
      if (done) { // swap-remove into the pool (the tail was already stepped this frame — iteration runs backwards)
        this.leafPool.push(l); this.burstLeaves.delete(l);
        const last = this.leaves.pop()!;
        if (i < this.leaves.length) this.leaves[i] = last;
        continue;
      }
      const lm = b.leaves[l.variant], k = counts[l.variant];
      if (k >= LEAF_CAP_PER_MESH) continue;
      const t = leafPose(l, this._pose);
      this._e.set(t.pitch, t.yaw, t.roll, LEAF_EULER);
      this._q.setFromEuler(this._e);
      this._p.set(t.x - b.anchorX, t.y - b.anchorY, t.z - b.anchorZ);
      this._s.set(l.size, l.size, l.size);
      this._m.compose(this._p, this._q, this._s);
      lm.mesh.setMatrixAt(k, this._m);
      lm.mesh.setColorAt(k, this._c.setHex(LEAF_TINTS[l.tint]));
      (lm.fade.array as Float32Array)[k] = leafAlpha(l, px, pz);
      counts[l.variant] = k + 1;
    }
    // a budget that fell to zero (left the woods, the flag off) lets the leaves in the air finish their fall and fade
    const hidden = atlas.interiorId !== null;
    for (let v = 0; v < b.leaves.length; v++) {
      const { mesh, fade } = b.leaves[v];
      mesh.count = counts[v];
      mesh.visible = counts[v] > 0 && !hidden;
      if (counts[v] > 0) { mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true; fade.needsUpdate = true; }
    }
  }

  /** The probe's close-up: `n` leaves just ahead of the camera, `h` m over the ground (they still obey every rule). */
  private leafBurst(n: number, h?: number, dMax = 6): number {
    const b = this.batches;
    if (!b || this.gate) return 0;
    const px = atlas.localX, pz = atlas.localZ, yaw = atlas.localYaw;
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw); // three's camera forward
    const room = LEAF_CAP_PER_MESH * b.leaves.length - this.leaves.length;
    const k = Math.max(0, Math.min(48, room, n | 0));
    this.burstLeaves.clear(); // the diagnostics follow the LATEST burst (an earlier one's landed leaves read as this one's)
    for (let i = 0; i < k; i++) {
      const l = spawnLeaf(this.rng, px, pz, this.floorFn, this.leafPool.pop());
      const d0 = Math.min(1.5, dMax * 0.4), d = d0 + this.rng() * Math.max(0, dMax - d0), side = (this.rng() - 0.5) * 0.9 * d;
      l.x = px + fx * d - fz * side; l.z = pz + fz * d + fx * side;
      l.y = this.floorAt(l.x, l.z) + (h ?? 1 + this.rng() * 3);
      this.leaves.push(l); this.burstLeaves.add(l);
      this.stats.leavesSpawned++;
    }
    return k;
  }

  private stepMotes(b: Batches, dt: number, px: number, pz: number, wx: number, wz: number, ws: number): void {
    const target = Math.min(this.budget.pollen, MOTE_CAP);
    let staying = 0;
    for (let i = 0; i < this.motes.length; i++) if (this.motes[i].m.leaving < 0) staying++;
    while (staying < target) {
      const m = spawnMote(this.rng, px, pz);
      const s = b.motes.add(0xfff1c4, m.size, 0, m.x, m.y, m.z); // born invisible: it fades in (it used to appear at full glow)
      if (!s) break;
      this.motes.push({ m, s });
      staying++;
      this.stats.motesSpawned++;
    }
    // a budget that fell releases its surplus to fade out — the motes used to be popped the frame it dropped
    for (let i = this.motes.length - 1; i >= 0 && staying > target; i--) { const m = this.motes[i].m; if (m.leaving < 0) { releaseMote(m); staying--; } }
    for (let i = this.motes.length - 1; i >= 0; i--) {
      const e = this.motes[i];
      stepMote(e.m, dt, px, pz, this.floorAt(e.m.x, e.m.z), wx, wz, ws);
      if (moteGone(e.m)) {
        b.motes.remove(e.s); this.stats.motesFaded++;
        const last = this.motes.pop()!;
        if (i < this.motes.length) this.motes[i] = last;
        continue;
      }
      e.s.x = e.m.x; e.s.y = e.m.y; e.s.z = e.m.z; e.s.size = e.m.size;
      // it twinkles as it turns in the sun — a fade, not a shrink. Full glow at the glint's peak: at 0.8 the motes over a
      // pale meadow were barely a speck in the world picture (2026-09-29)
      b.motes.setOpacity(e.s, moteGlint(e.m) * moteAlpha(e.m, px, pz));
    }
  }

  private stepDevil(b: Batches, dt: number, px: number, pz: number, wx: number, wz: number, ws: number): void {
    // a devil the lever forced lives out its life: the budget's wind rule (clear weather has too little wind) cut it
    // down at birth — it faded out before it had faded in, peaking at half its dust
    const allowed = this.budget.devil || ((this.forceDevil || this.devilForced) && (this.lastEnv?.particleCap ?? 0) > 0);
    if (!this.devil) {
      this.hearDevil(null, 0, px, pz);
      if (!allowed) { this.devilWait = Math.max(this.devilWait, 4); return; }
      this.devilWait -= dt;
      if (this.devilWait > 0) return;
      this.devil = spawnDevil(this.rng, px, pz, wx, wz);
      if (this.forceDevil) { const a = this.rng() * Math.PI * 2; this.devil.x = px + Math.cos(a) * 45; this.devil.z = pz + Math.sin(a) * 45; }
      this.devilForced = this.forceDevil;
      this.forceDevil = false;
      this.stats.devils++;
      for (let i = 0; i < DEVIL.grains + DEVIL.skirt; i++) { const s = b.dust.add(i < DEVIL.grains ? GRAIN_HEX : SKIRT_HEX, 1, 0, px, 0, pz); if (s) this.devilSprites.push(s); }
    }
    const v = this.devil;
    stepDevil(v, dt, wx, wz, Math.max(ws, 0.5));
    // a devil that loses its wind (or its desert) dies early, fading
    if (!allowed && v.life - v.age > DEVIL.fadeOut) v.life = v.age + DEVIL.fadeOut;
    if (v.age >= v.life) { this.endDevil(b); this.devilWait = devilGap(this.rng); this.hearDevil(null, 0, px, pz); return; }
    const gy = this.groundAt(v.x, v.z);
    const n = Math.min(DEVIL.grains, this.devilSprites.length);
    for (let i = 0; i < this.devilSprites.length; i++) {
      const gr = i < n ? devilGrain(v, i, n, gy, this._gr) : devilSkirt(v, i - n, this.devilSprites.length - n, gy, this._gr);
      const s = this.devilSprites[i];
      s.x = gr.x; s.y = gr.y; s.z = gr.z; s.size = gr.size;
      b.dust.setOpacity(s, gr.alpha); // the column fades in and out — a real fade (the batch's per-sprite opacity)
    }
    this.hearDevil(v, gy, px, pz);
  }

  /** The devil's wind: one positional loop at its column; its gain is `devilHeard` (life × inverse distance). */
  private hearDevil(v: Devil | null, gy: number, px: number, pz: number): void {
    if (!v) { this.devilGain = 0; if (this.devilLoop) this.devilLoop.set(0, 1, 1.0); return; }
    const gain = devilHeard(devilAlpha(v), Math.hypot(v.x - px, v.z - pz));
    this.devilGain = gain;
    if (gain <= 0.005 && !this.devilLoop) return;
    this.devilLoop ??= sfxLoop('ambient_dust_devil', SOUND_SCOPE);
    this._at.x = v.x; this._at.y = gy + 4; this._at.z = v.z;
    this.devilLoop.at(this._at);
    this.devilLoop.set(gain, 0.9 + 0.2 * Math.min(1, devilAlpha(v)), 0.4);
  }

  private endDevil(b: Batches | null): void {
    if (b) for (const s of this.devilSprites) b.dust.remove(s);
    this.devilSprites.length = 0;
    this.devil = null;
    this.devilForced = false;
  }

  private clearAll(): void {
    const b = this.batches;
    for (const l of this.leaves) this.leafPool.push(l);
    this.leaves.length = 0; this.motes.length = 0; this.burstLeaves.clear();
    this.devilSprites.length = 0; this.devil = null; this.devilGain = 0; this.devilForced = false;
    if (this.devilLoop) { stopSfxScope(SOUND_SCOPE); this.devilLoop = null; }
    if (!b) return;
    for (const bb of [b.motes, b.dust]) { bb.clear(); bb.commit(); this.scene?.remove(bb.sprite); deferDisposeTree(bb.sprite); }
    this.scene?.remove(b.group);
    deferDisposeTree(b.group);
    this.batches = null;
  }

  private diag(): Record<string, unknown> {
    const v = this.devil;
    let settled = 0, settledA = 0, leaving = 0, motesA = 0, aloftFull = 0, restingFull = 0, dissolving = 0;
    for (const l of this.leaves) {
      const a = leafAlpha(l, atlas.localX, atlas.localZ);
      if (l.settled >= 0) { settled++; settledA += a; if (a >= 0.99) restingFull++; else if (a > 0.15 && a < 0.85) dissolving++; } else if (a >= 0.99) aloftFull++;
    }
    const near = this.leaves.map((l) => ({ d: +Math.hypot(l.x - atlas.localX, l.y - atlas.localY, l.z - atlas.localZ).toFixed(2), a: +leafAlpha(l, atlas.localX, atlas.localZ).toFixed(2), s: +l.settled.toFixed(2) })).sort((p, q) => p.d - q.d).slice(0, 6);
    for (const e of this.motes) { if (e.m.leaving >= 0) leaving++; motesA += moteAlpha(e.m, atlas.localX, atlas.localZ); }
    return {
      gate: this.gate, budget: this.budget, env: this.lastEnv, held: this.held, timeScale: this.timeScale,
      leaves: this.leaves.length, leavesSettled: settled, settledAlpha: settled ? +(settledA / settled).toFixed(3) : null,
      aloftFull, restingFull, dissolving, near, burst: this.burstDiag(),
      motes: this.motes.length, motesLeaving: leaving, motesAlpha: this.motes.length ? +(motesA / this.motes.length).toFixed(3) : null,
      devil: v ? {
        x: Math.round(v.x), z: Math.round(v.z), age: +v.age.toFixed(1), life: +v.life.toFixed(1), alpha: +devilAlpha(v).toFixed(3),
        grains: this.devilSprites.length, distM: Math.round(Math.hypot(v.x - atlas.localX, v.z - atlas.localZ)),
        gain: +this.devilGain.toFixed(3), heard: this.devilGain > 0.005, rangeM: DEVIL_SOUND.rangeM,
      } : null,
      nearestLeafM: this.leaves.reduce((m, l) => Math.min(m, Math.hypot(l.x - atlas.localX, l.y - atlas.localY, l.z - atlas.localZ)), Infinity),
      nearestMoteM: this.motes.reduce((m, e) => Math.min(m, Math.hypot(e.m.x - atlas.localX, e.m.y - atlas.localY, e.m.z - atlas.localZ)), Infinity),
      draws: this.batches ? [...this.batches.leaves.map((x) => x.mesh), this.batches.motes.sprite, this.batches.dust.sprite].filter((o) => o.visible).length : 0,
      stats: { ...this.stats },
    };
  }

  private burstDiag(): { n: number; aloftFull: number; restingFull: number; dissolving: number } {
    let n = 0, aloftFull = 0, restingFull = 0, dissolving = 0;
    for (const l of this.burstLeaves) {
      n++;
      const a = leafAlpha(l, atlas.localX, atlas.localZ);
      if (l.settled < 0) { if (a >= 0.99) aloftFull++; } else if (a >= 0.99) restingFull++; else if (a > 0.15 && a < 0.85) dissolving++;
    }
    return { n, aloftFull, restingFull, dissolving };
  }

  dispose(): void { this.clearAll(); }
}
